/**
 * Store Types Configuration
 * 
 * This file contains all the available store types that can be selected
 * when creating or updating a store. These types help categorize stores
 * and may be used for analytics, filtering, and store-specific features.
 */

export const STORE_TYPES = [
  // General & Kirana Stores (Most Common)
  "General Store",
  "Kirana Store",
  "Kirana Wholesaler",
  "Kirana Wholesaler (Ration & Masala)",
  "Kirana Wholesaler (FMCG/Packaged Goods)",
  "Kirana Vendor / Hawker",
  
  // Food & Beverage
  "Dairy Items & Milk Booth",
  "Vegetable & Fruit Shop",
  "Sweet Shop and Bakery",
  "Tea & Snacks Stall",
  "Paan Shop",
  
  // Health & Personal Care
  "Medical/Pharmacy",
  "Cosmetics & Beauty Store",
  "Optical & Eyewear Store",
  
  // Clothing & Accessories
  "Clothing & Garments",
  "Footwear & Shoe Shop",
  "Jewellery & Gold Shop",
  
  // Electronics & Technology
  "Electronics & Mobile Shop",
  "Photocopy, Printing & Internet Cafe",
  
  // Home & Construction
  "Hardware, Plywood & Paint Store",
  "Building Materials & Cement",
  "Utensils & Kitchenware Shop",
  
  // Services
  "Dry Cleaners & Laundry",
  
  // Automotive
  "Automobile Parts & Accessories",
  
  // Education & Gifts
  "Stationery & Book Store",
  "Gift & Toy Shop",
  
  // Other
  "Other"
] as const;

export type StoreType = typeof STORE_TYPES[number];


/**
 * Get all store types as a flat array
 */
export const getAllStoreTypes = () => STORE_TYPES;

/**
 * Check if a store type is valid
 */
export const isValidStoreType = (type: string): type is StoreType => {
  return STORE_TYPES.includes(type as StoreType);
};

/**
 * Normalize store type from database to a valid store type
 * Falls back to "General Store" if the store type doesn't match any valid type
 * 
 * @param storeType - The store type from the database (may be invalid)
 * @returns A valid store type, defaulting to "General Store" if invalid
 */
export const normalizeStoreType = (storeType: string | null | undefined): StoreType => {
  if (!storeType || typeof storeType !== 'string') {
    return 'General Store';
  }

  const trimmed = storeType.trim();
  
  // Check exact match first (case-sensitive)
  if (isValidStoreType(trimmed)) {
    return trimmed;
  }

  // Try case-insensitive match
  const lowerTrimmed = trimmed.toLowerCase();
  for (const validType of STORE_TYPES) {
    if (validType.toLowerCase() === lowerTrimmed) {
      return validType;
    }
  }

  // Fallback to "General Store" for any invalid store type
  return 'General Store';
}; 